import { useState } from 'react';
import { supabase } from '../lib/supabase';

export default function Auth() {
  const [mode, setMode] = useState('signup'); // 'signup' | 'signin'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (mode === 'signup') {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } }
      });
      if (error) setError(error.message);
      else setMessage('Check your email to confirm your account.');
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    }

    setLoading(false);
  };

  const style = `
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,900;1,900&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Plus Jakarta Sans', sans-serif; background: #FAF7F2; }

    .auth-wrap {
      min-height: 100vh; display: flex; align-items: center; justify-content: center;
      padding: 24px; background: #FAF7F2;
      background-image: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(139,92,246,0.06) 0%, transparent 70%);
    }
    .auth-card {
      width: 100%; max-width: 440px;
      background: #fff; border-radius: 24px; padding: 48px 40px;
      box-shadow: 0 4px 40px rgba(0,0,0,0.08);
    }
    .auth-logo {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 24px;
      color: #1E1040; text-decoration: none; display: block; margin-bottom: 32px;
    }
    .auth-logo span { color: #8B5CF6; font-style: italic; }
    .auth-title {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 28px;
      color: #1E1040; margin-bottom: 8px; line-height: 1.1;
    }
    .auth-sub { font-size: 14px; color: #6B7280; margin-bottom: 32px; }
    .auth-field { margin-bottom: 16px; }
    .auth-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
    .auth-input {
      width: 100%; padding: 12px 16px; border: 1.5px solid #E5E7EB;
      border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 15px; color: #1E1040; outline: none;
      transition: border-color 0.2s;
      background: #FAFAFA;
    }
    .auth-input:focus { border-color: #8B5CF6; background: #fff; }
    .auth-btn {
      width: 100%; padding: 14px; border: none; border-radius: 12px;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      color: #fff; font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700; font-size: 16px; cursor: pointer; margin-top: 8px;
      transition: opacity 0.2s, transform 0.2s;
      box-shadow: 0 4px 16px rgba(139,92,246,0.3);
    }
    .auth-btn:hover { opacity: 0.9; transform: translateY(-1px); }
    .auth-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
    .auth-error {
      background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
      border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626;
      margin-bottom: 16px;
    }
    .auth-message {
      background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.2);
      border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #16A34A;
      margin-bottom: 16px;
    }
    .auth-switch { text-align: center; margin-top: 24px; font-size: 14px; color: #6B7280; }
    .auth-switch button {
      background: none; border: none; color: #8B5CF6; font-weight: 700;
      cursor: pointer; font-size: 14px; font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .auth-divider { border: none; border-top: 1px solid #F3F4F6; margin: 24px 0; }
    .auth-trial {
      display: flex; align-items: center; gap: 8px; font-size: 12px; color: #9CA3AF;
      justify-content: center; margin-top: 20px;
    }
    .auth-trial span { color: #8B5CF6; font-weight: 600; }
  `;

  return (
    <>
      <style>{style}</style>
      <div className="auth-wrap">
        <div className="auth-card">
          <a href="/" className="auth-logo">Merq<span>Legacy</span></a>

          <h1 className="auth-title">
            {mode === 'signup' ? 'Create your vault.' : 'Welcome back.'}
          </h1>
          <p className="auth-sub">
            {mode === 'signup'
              ? 'Your family deserves to have everything organized. Set up your vault today.'
              : 'Sign in to access your Legacy vault.'}
          </p>

          {error && <div className="auth-error">{error}</div>}
          {message && <div className="auth-message">{message}</div>}

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <div className="auth-field">
                <label className="auth-label">Full Name</label>
                <input
                  className="auth-input"
                  type="text"
                  placeholder="Jane Smith"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                className="auth-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input
                className="auth-input"
                type="password"
                placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? 'Please wait...' : mode === 'signup' ? 'Create My Vault — $69/year' : 'Sign In'}
            </button>
          </form>

          <div className="auth-switch">
            {mode === 'signup'
              ? <>Already have an account? <button onClick={() => setMode('signin')}>Sign in</button></>
              : <>Don't have an account? <button onClick={() => setMode('signup')}>Start free trial</button></>
            }
          </div>

          {mode === 'signup' && (
            <div className="auth-trial">
              <span>30-day money-back guarantee</span> · $69/year · Cancel anytime
            </div>
          )}
        </div>
      </div>
    </>
  );
}
